import React, { useEffect, useState } from "react";
import api from "../lib/api";
import VideoCard from "../components/VideoCard";
import PageHeroBanner from "../components/PageHeroBanner";
import { WEATHER_HOTSPOTS } from "../components/heroBannerHotspots";
import { Cloud, Radar, AlertTriangle, CalendarDays } from "lucide-react";

/**
 * /browse/weather — Tap IN Weather.
 *
 * Stub page with 4 in-page anchor sections matching the banner hotspots:
 *  - #local       Local Forecasts
 *  - #radar       Live Radar
 *  - #alerts      Severe Alerts
 *  - #sevenday    7-Day Outlook
 *
 * Plus a row of any videos tagged in the `weather` category so the page
 * doubles as the existing browse experience.
 */
export default function Weather() {
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api.get("/videos?category=weather&limit=24")
      .then((r) => setVideos(r.data || []))
      .catch(() => setVideos([]));
  }, []);

  // Best-effort browser geolocation → label only; no API calls
  const [city, setCity] = useState("Your area");
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition((p) => {
      const lat = p.coords.latitude.toFixed(2);
      const lng = p.coords.longitude.toFixed(2);
      setCity(`Lat ${lat}, Lng ${lng}`);
    }, () => {}, { timeout: 4000 });
  }, []);

  return (
    <>
      <PageHeroBanner
        src="/tapin-weather-hero.png"
        alt="Tap IN TV Weather — Your Weather. Your World."
        testid="weather-page-hero"
        hotspots={WEATHER_HOTSPOTS}
      />
      <div data-testid="weather-page" className="px-4 sm:px-8 md:px-12 pb-20 max-w-7xl mx-auto space-y-12">
        <Section id="local" icon={Cloud} eyebrow="Local Forecasts" title="What's happening right now" testid="weather-local">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[{ k: "Now",  v: "72°", note: "Mostly sunny" },
              { k: "Today",v: "76°", note: "High · light wind" },
              { k: "Tonight", v: "58°", note: "Clear skies" },
              { k: "Humidity", v: "44%", note: "Comfortable" }].map((m) => (
              <div key={m.k} className="rounded-2xl tv-glass p-4">
                <div className="text-white/50 text-xs uppercase tracking-wider">{m.k}</div>
                <div className="font-display font-black text-3xl text-white mt-1">{m.v}</div>
                <div className="text-white/60 text-xs mt-1">{m.note}</div>
              </div>
            ))}
          </div>
          <div className="text-white/40 text-xs mt-3">{city} · placeholder — connect a weather API to see real values</div>
        </Section>

        <Section id="radar" icon={Radar} eyebrow="Live Radar" title="Track the storm" testid="weather-radar">
          <div className="rounded-2xl tv-glass aspect-[16/9] flex items-center justify-center text-white/40">
            <Radar className="w-16 h-16" />
          </div>
        </Section>

        <Section id="alerts" icon={AlertTriangle} eyebrow="Severe Alerts" title="Stay informed in real time" testid="weather-alerts">
          <div className="rounded-2xl bg-amber-500/10 border border-amber-400/30 p-4 text-amber-200 text-sm">
            No active alerts in your area.
          </div>
        </Section>

        <Section id="sevenday" icon={CalendarDays} eyebrow="7-Day Outlook" title="Plan your week" testid="weather-7day">
          <div className="grid grid-cols-7 gap-2">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, i) => (
              <div key={d} className="rounded-xl tv-glass p-3 text-center">
                <div className="text-white/50 text-[10px] uppercase tracking-wider">{d}</div>
                <div className="font-display font-black text-xl text-white mt-1">{72 - i}°</div>
              </div>
            ))}
          </div>
        </Section>

        {videos.length > 0 && (
          <Section id="videos" icon={Cloud} eyebrow="On Tap IN Weather" title="Latest videos">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {videos.map((v) => <VideoCard key={v.id} item={v} fullWidth />)}
            </div>
          </Section>
        )}
      </div>
    </>
  );
}

function Section({ id, icon: Icon, eyebrow, title, testid, children }) {
  return (
    <section id={id} data-testid={testid} className="scroll-mt-32">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-5 h-5 text-tv-accent" />
        <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold">{eyebrow}</div>
      </div>
      <h2 className="font-display font-black text-3xl text-white tracking-tighter mb-4">{title}</h2>
      {children}
    </section>
  );
}
